import { store } from '../../store.js';
import { validateEmail } from '../../utils.js';
import { createSelector } from 'reselect';
import * as selectors from './selectors';
import * as manageAccount from '../manage-account';
import i18next from '@dw/i18next-esm';

/**
 * Get formatted error message based on remaining limit
 * @param {String} accountId 
 * @param {Number} count - Number of invitations user is trying to send
 * @param {String} context - 'account' or 'board'
 */
const getErrorMessage = createSelector(
  (state, accountId, count) => count,
  (state, accountId, count) => selectors.remainingLimit(state, accountId),
  (state, accountId, count) => selectors.dailyLimit(state, accountId),
  (count, remaining, limit) => {
    if(!count) {
      return null;
    }

    // Unlimited invitations
    if(limit === -1) {
      return null;
    }

    // Admin has disabled invitations
    if (limit === 0) {
      return i18next.t('manage-account:dailyInvitationQuota.errorMessages.adminDisabled');
    }

    // No more invitations today
    if (!remaining) {
      return i18next.t('manage-account:dailyInvitationQuota.errorMessages.noMore');
    }

    // Attempting to invite more than remaining
    if (count > remaining) {
      return i18next.t('manage-account:dailyInvitationQuota.errorMessages.exceeded', { count: remaining });
    }

    return null;
  }
);

/**
 * Calculates the number of new users (users not in account team)
 * @param {Array<string>} emails - Array of email addresses
 * @param {string} accountId - Account ID
 * @returns {number} Count of new users
 */
export const calculateNewUserCount = (emails, accountId) => {
  if (!emails || emails.length === 0 || !accountId) {
    return 0;
  }

  const state = store.getState();
  const validEmails = emails.filter(email => {
    email = email && email.trim() || '';
    return email && validateEmail(email) && !manageAccount.selectors.isAccountMember({ state, accountId, email });
  });

  return validEmails.length;
}

/**
 * Gets the invitation quota error message based on account ID and new user count
 * @param {string} accountId - Account ID
 * @param {number} newUserCount - Number of new users being added
 * @returns {string} Error message or empty string if no error
 */
export const getInviteErrorMessage = (accountId, newUserCount) => {
  const state = store.getState();
  return getErrorMessage(state, accountId, newUserCount) || '';
}
